import tokens from '@kluein/design-tokens';
import {
  Anchor,
  Avatar,
  UnstyledButton,
  type AvatarProps,
} from '@mantine/core';
import {
  useCallback,
  useEffect,
  useState,
  type MouseEventHandler,
  type ReactNode,
} from 'react';

const ICON_SIZES: Record<string, string> = {
  xxsmall: tokens.iconSize.xxsmall,
  xsmall: tokens.iconSize.xsmall,
  small: tokens.iconSize.small,
  medium: tokens.iconSize.medium,
  large: tokens.iconSize.large,
  xlarge: tokens.iconSize.xlarge,
  xxlarge: tokens.iconSize.xxlarge,
};

type LogoProps = {
  a11yTitle?: string;
  background?: string;
  border?: boolean;
  'data-test-id'?: string;
  'data-test-target'?: string;
  'data-tracking-id'?: string;
  href?: string;
  onClick?: MouseEventHandler;
  onError?: () => void;
  size?: string;
  src?: string;
} & Omit<AvatarProps, 'src' | 'size'>;

const resolveSize = (size: string): number | string => {
  const mapped = ICON_SIZES[size];
  if (mapped) {
    return parseInt(mapped, 10);
  }

  const numeric = parseInt(size, 10);
  if (!isNaN(numeric) && String(numeric) + 'px' === size) {
    return numeric;
  }

  // Preserve non-numeric values like "100%" as-is
  if (!isNaN(numeric) && size.endsWith('px')) {
    return numeric;
  }

  return size;
};

const Logo = ({
  a11yTitle = '',
  background = tokens.color.neutral[100],
  border = false,
  'data-test-id': dataTestId,
  'data-test-target': dataTestTarget,
  'data-tracking-id': dataTrackingId,
  href,
  onClick,
  onError,
  size = 'large',
  src,
  ...rest
}: LogoProps): ReactNode => {
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  useEffect(() => {
    if (src) {
      setHasError(false);
    } else {
      handleError();
    }
  }, [handleError, src]);

  const resolved = resolveSize(size);
  const isNumericSize = typeof resolved === 'number';
  const showBorder = border || hasError;
  const isInteractive = !!(href || onClick);

  const sizeStyles = isNumericSize
    ? {}
    : { width: resolved, height: resolved, minWidth: resolved };

  const avatar = (
    <Avatar
      src={hasError ? null : src}
      alt={a11yTitle}
      name={a11yTitle}
      color="initials"
      radius="xs"
      size={isNumericSize ? resolved : 32}
      bg={background}
      styles={{
        root: {
          flexShrink: 0,
          border:
            showBorder && !isInteractive
              ? `1px solid ${tokens.color.neutral[500]}`
              : undefined,
          ...sizeStyles,
        },
        image: {
          objectFit: 'contain',
          padding: 1,
        },
      }}
      imageProps={{ onError: handleError }}
      data-test-id={dataTestId}
      data-test-target={dataTestTarget}
      data-tracking-id={!isInteractive ? dataTrackingId : undefined}
      {...rest}
    />
  );

  if (href) {
    return (
      <Anchor
        href={href}
        onClick={onClick}
        underline="never"
        data-tracking-id={dataTrackingId}
        style={{
          display: 'inline-flex',
          border: showBorder
            ? `1px solid ${tokens.color.neutral[500]}`
            : undefined,
          borderRadius: 'var(--mantine-radius-xs)',
          lineHeight: 0,
        }}
      >
        {avatar}
      </Anchor>
    );
  }

  if (onClick) {
    return (
      <UnstyledButton
        onClick={onClick}
        data-tracking-id={dataTrackingId}
        style={{
          display: 'inline-flex',
          border: showBorder
            ? `1px solid ${tokens.color.neutral[500]}`
            : undefined,
          borderRadius: 'var(--mantine-radius-xs)',
          lineHeight: 0,
        }}
      >
        {avatar}
      </UnstyledButton>
    );
  }

  return avatar;
};

export default Logo;
